import React, { useState, useEffect } from 'react';
import { useDashboardAccessNotifications } from '@/hooks/useDashboardAccessNotifications';
import { DashboardAccessNotificationModal } from './DashboardAccessNotificationModal';
import { useAuth } from '@/hooks/useAuth';

interface DashboardAccessNotificationProviderProps {
  children: React.ReactNode;
}

// Authorized emails for dashboard access notifications
const AUTHORIZED_EMAILS = [
  'lcosta@divuslegalgroup.com',
  'njunior@divuslegalgroup.com',
  'lucascosta.us@gmail.com'
];

export const DashboardAccessNotificationProvider: React.FC<DashboardAccessNotificationProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const { notifications } = useDashboardAccessNotifications();
  const [currentNotification, setCurrentNotification] = useState<typeof notifications[0] | null>(null);
  const [notifiedCodes, setNotifiedCodes] = useState<Set<string>>(new Set());

  // Check if user is authorized (case-insensitive email check)
  const isAuthorized = user?.email && AUTHORIZED_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    if (!isAuthorized || notifications.length === 0) return;

    // Find the newest notification that hasn't been shown yet
    const newNotification = notifications.find(n => !notifiedCodes.has(n.id));

    if (newNotification) {
      setCurrentNotification(newNotification);
      setNotifiedCodes(prev => new Set([...prev, newNotification.id]));
    }
  }, [notifications, isAuthorized, notifiedCodes]);

  const handleClose = () => {
    setCurrentNotification(null);
  };

  return (
    <>
      {children}
      {isAuthorized && (
        <DashboardAccessNotificationModal
          notification={currentNotification}
          open={!!currentNotification}
          onClose={handleClose}
        />
      )}
    </>
  );
};
